import React, { useState } from "react";
import { TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";
import { Typography, Stack, Box } from "@mui/material";
import {
  ShieldOutlined, CheckCircleOutline, DevicesOutlined, FingerprintOutlined,
} from "@mui/icons-material";
import { deviceStore } from "../mock/deviceStore";

function _parseDeviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iphone/i.test(ua)) return "iPhone";
  if (/ipad/i.test(ua)) return "iPad";
  if (/android/i.test(ua)) return "Android Device";
  if (/macintosh|mac os x/i.test(ua)) return "Mac (Browser)";
  if (/windows nt/i.test(ua)) return "Windows (Browser)";
  return "Browser";
}

const InfoRow = ({ icon: Icon, label, value, mono }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Icon sx={{ fontSize: 16, color: "var(--text-2)", mt: "2px", flexShrink: 0 }} />
    <Box>
      <Typography
        sx={{
          fontSize: "10px", fontWeight: 700, color: "var(--text-2)",
          textTransform: "uppercase", letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px", color: "var(--text-1)", fontWeight: 500,
          fontFamily: mono ? "monospace" : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Stack>
);

const ValidateDevice = ({ onRequestSent }) => {
  const { analystId, analystName } = useSelector((state) => ({
    analystId: state.auth.analyst_id,
    analystName: state.auth.analystName,
  }));

  // Generate a stable fake MAC for this session
  const [macAddress] = useState(() => deviceStore.genMac());
  const [submitted, setSubmitted] = useState(false);

  const handleRequest = () => {
    deviceStore.addRequest({ analystId, analystName, macAddress });
    setSubmitted(true);
    setTimeout(() => {
      if (onRequestSent) onRequestSent();
    }, 1200);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh", width: "100%",
        backgroundColor: "var(--app-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 420, width: "100%",
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "16px",
          p: { xs: 3, sm: 4 },
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 64, height: 64, borderRadius: "16px",
            backgroundColor: "rgba(232, 108, 0, 0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            mb: 3, mx: "auto",
          }}
        >
          <ShieldOutlined sx={{ fontSize: 32, color: "rgba(232, 108, 0, 1)" }} />
        </Box>

        <Typography
          sx={{
            fontSize: "22px", fontWeight: 700, color: "var(--text-1)",
            textAlign: "center", mb: 1,
          }}
        >
          Validate Your Device
        </Typography>
        <Typography
          sx={{
            fontSize: "14px", color: "var(--text-2)", textAlign: "center",
            mb: 3, lineHeight: 1.6,
          }}
        >
          This device hasn't been registered yet. Request access from your admin to continue.
        </Typography>

        {/* Device info */}
        <Box
          sx={{
            backgroundColor: "var(--surface-2)",
            border: "1px solid var(--border-1)",
            borderRadius: "10px", p: 2, mb: 3,
          }}
        >
          <Stack spacing={1.5}>
            <InfoRow
              icon={DevicesOutlined}
              label="Device"
              value={_parseDeviceLabel()}
            />
            <Box sx={{ height: "1px", backgroundColor: "var(--border-1)" }} />
            <InfoRow
              icon={FingerprintOutlined}
              label="MAC Address"
              value={macAddress}
              mono
            />
            <Box sx={{ height: "1px", backgroundColor: "var(--border-1)" }} />
            <Stack direction="row" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: "10px", fontWeight: 700, color: "var(--text-2)",
                    textTransform: "uppercase", letterSpacing: 0.5, mb: "2px",
                  }}
                >
                  Analyst
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "var(--text-1)", fontWeight: 500 }}>
                  {analystName || `Analyst #${analystId}`}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: "10px", fontWeight: 700, color: "var(--text-2)",
                    textTransform: "uppercase", letterSpacing: 0.5, mb: "2px",
                  }}
                >
                  Analyst ID
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "var(--text-1)", fontWeight: 500 }}>
                  #{analystId}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Action */}
        {submitted ? (
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
              py: 1.5, borderRadius: "10px",
              backgroundColor: "rgba(22,163,74,0.08)",
              border: "1px solid rgba(22,163,74,0.25)",
            }}
          >
            <CheckCircleOutline sx={{ fontSize: 20, color: "rgba(22,163,74,1)" }} />
            <Typography sx={{ fontSize: "14px", color: "rgba(22,163,74,1)", fontWeight: 600 }}>
              Request sent! Redirecting…
            </Typography>
          </Box>
        ) : (
          <TouchableOpacity onPress={handleRequest}>
            <Box
              sx={{
                py: 1.6, borderRadius: "10px",
                backgroundColor: "rgba(232, 108, 0, 1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Typography sx={{ fontSize: "15px", color: "#fff", fontWeight: 700 }}>
                Request Access
              </Typography>
            </Box>
          </TouchableOpacity>
        )}

        <Typography
          sx={{
            fontSize: "12px", color: "var(--text-2)", textAlign: "center",
            mt: 2, lineHeight: 1.5,
          }}
        >
          Your admin will review the request and approve your device.
        </Typography>
      </Box>
    </Box>
  );
};

export default ValidateDevice;
